import { getClassFunction } from '@helpers/functions';
import GetComponentMarkup from '@helpers/getComponentMarkup';
import DropdownSelectionItem from '@atoms/dropdown-selection-item/DropdownSelectionItem';

const getClass = getClassFunction('kf-a-dropdown');

window.KFDropdown = window.KFDropdown || {
  elements: document.querySelectorAll<HTMLElement>(getClass(undefined, { checkAttached: true })),
  init() {
    this.elements = document.querySelectorAll<HTMLElement>(getClass(undefined, { checkAttached: true }));
    [].forEach.call(this.elements, (el) => this.setupDropdown(el));
  },

  setupDropdown(el: HTMLElement) {
    if (!el) return;
    el.dataset.eventAttached = 'true';

    const dropdownLabel = el.querySelector<HTMLDivElement>(getClass('label'));
    const dropdownLabelText = el.querySelector<HTMLElement>(getClass('label-text'));
    const dropdownArrow = el.querySelector<HTMLElement>(getClass('arrow'));
    const dropdownOptions = el.querySelector<HTMLElement>(getClass('options'));
    const options = el.querySelectorAll<HTMLElement>(getClass('option'));
    const defaultLabel = dropdownLabelText?.textContent;
    const inputs = el.querySelectorAll<HTMLInputElement>(`${getClass('option')} input`);
    const optiDropdownId = el.dataset.optiDropdownId;
    const mainFormBody = el.closest('.Form__MainBody');
    const isMultipleChoice = el.classList.contains('kf-a-dropdown--checkbox-checkmark');

    const optiDropdown = mainFormBody?.querySelector<HTMLSelectElement>(`select[name="${optiDropdownId}"]`);

    const checkOptiFormVisible = () => {
      if (!mainFormBody) return;

      const observer = new MutationObserver((mutations: MutationRecord[]) => {
        const record = mutations[0];
        const target = record.target as HTMLDivElement;
        const targetDisplay = getComputedStyle(target).display;

        if (targetDisplay === 'none') {
          if (isMultipleChoice) {
            handleCheckboxChange();
          } else {
            el.dispatchEvent(
              new CustomEvent('reset', {
                detail: {
                  noSelect: true,
                },
              })
            );
          }
        }
      });

      observer.observe(mainFormBody, { attributeOldValue: true, attributeFilter: ['style'] });
    };

    const closeDropdown = () => {
      dropdownArrow?.classList.remove('active');
      dropdownOptions?.classList.remove('open');
      el.setAttribute('aria-expanded', 'false');
    };

    const toggleDropdown = () => {
      dropdownArrow?.classList.toggle('active');
      dropdownOptions?.classList.toggle('open');
      el.setAttribute('aria-expanded', dropdownOptions?.classList.contains('open') ? 'true' : 'false');
    };

    if (!dropdownLabel || !dropdownOptions || !dropdownLabelText) return;

    el.addEventListener('reset', (e: Event) => {
      const customEvent = e as CustomEvent<{ noSelect?: boolean; value?: string }>;

      if (customEvent.detail?.noSelect) {
        //clear the selected value
        [].forEach.call(options, (option: HTMLElement) => {
          const input = option.querySelector<HTMLInputElement>('input');

          if (input) {
            input.checked = false;
          }
        });
        if (dropdownLabelText) {
          dropdownLabelText.textContent = defaultLabel ?? '';
        }
        dropdownLabelText.removeAttribute('data-filled');

        return;
      }
      const value = customEvent.detail?.value;

      if (!value) return options![0].querySelector('label')?.click();
      for (const option of options) {
        const input = option.querySelector<HTMLInputElement>('input');

        if (input?.value === value) return option.querySelector('label')?.click();
      }
      options[0].querySelector('label')?.click();
    });

    const onOptionClick = (option: HTMLElement, event?: MouseEvent) => {
      const input = option.querySelector<HTMLInputElement>('input');

      event?.stopPropagation();

      if (!input) return;

      if (!isMultipleChoice) {
        handleRadioChange(option, input);
      } else {
        handleCheckboxChange(option, input);
      }
    };

    const preselectSingleChoice = () => {
      const preselectInput = [...inputs].find((input) => input.checked && !input.disabled);

      if (!preselectInput) {
        if (optiDropdown) {
          // As select always has a default value, we need to set the default value to empty string.
          // This is to prevent the select from showing the first option as selected when no option is selected in the dropdown.
          optiDropdown.value = '';
        }

        return;
      }
      const preselectOption = preselectInput.closest<HTMLElement>(getClass('option'));

      if (!preselectOption) return;

      handleRadioChange(preselectOption, preselectInput);
    };

    const clearOptionsSelection = () => {
      [].forEach.call(options, (option: HTMLElement) => {
        option.setAttribute('aria-selected', 'false');
      });
    };

    const handleRadioChange = (option: HTMLElement, input: HTMLInputElement) => {
      dropdownLabelText.textContent = option.textContent;
      if (input.disabled) {
        input.disabled = false;
      }

      if (!input.value) {
        dropdownLabelText.removeAttribute('data-filled');
      } else {
        dropdownLabelText.dataset.filled = 'true';
      }

      const value = input.value || '';

      clearOptionsSelection();
      option.setAttribute('aria-selected', 'true');

      closeDropdown();

      if (optiDropdown) {
        optiDropdown.value = value;

        // trigger change event, this is particular for Optimizely Forms
        optiDropdown.dispatchEvent(new Event('change', { bubbles: true }));
      }

      setTimeout(() => {
        el.dispatchEvent(
          new CustomEvent('dropdown-change', {
            detail: {
              value: value,
              text: option.textContent,
              dataset: input?.dataset,
            },
          })
        );
      }, 0);
    };

    const handleTabSelectionView = () => {
      const isTabSelection = el.classList.contains('kf-a-dropdown--tab-selection');

      if (!isTabSelection) return;

      if (document.body.offsetWidth < 768) {
        el.dataset.tabViewEnabled = 'true';

        return;
      }

      const isLayoutWrapped = shadowCheckTabSelectionViewWrapped();

      if (isLayoutWrapped) {
        el.dataset.tabViewEnabled = '';
      } else {
        el.dataset.tabViewEnabled = 'true';
      }
    };

    const shadowCheckTabSelectionViewWrapped = () => {
      // This is because dropdown's layout will be changed everytime tabViewEnabled change, so need to render it in tab view mode to check
      // if the layout wrapped or not.

      const dropdownCloned = el.cloneNode(true) as HTMLElement;

      dropdownCloned.dataset.tabViewEnabled = 'true';
      dropdownCloned.style.visibility = 'hidden';
      dropdownCloned.style.pointerEvents = 'none';
      const clonedInputs = dropdownCloned.querySelectorAll<HTMLInputElement>('input');

      // prevent multiple inputs with same id/name inside dom.
      clonedInputs.forEach((input) => {
        input.id = '';
        input.name = '';
      });

      el.parentElement?.insertBefore?.(dropdownCloned, el.nextSibling);
      const clonedOptions = dropdownCloned.querySelectorAll<HTMLElement>('.kf-a-dropdown__option');
      const firstTab = clonedOptions.item(0);
      const lastTab = clonedOptions.item(clonedOptions.length - 1);
      const isLayoutWrapped = firstTab.offsetTop !== lastTab.offsetTop;

      el.parentElement?.removeChild(dropdownCloned);

      return isLayoutWrapped;
    };

    const handleCheckboxChange = (option?: HTMLElement, input?: HTMLInputElement) => {
      setTimeout(() => {
        const checkedInputs = [...inputs].filter((input) => input.checked && input.value);
        const values = checkedInputs.map((input) => input.value);

        dropdownLabelText.innerHTML = '';

        values.forEach((value) => {
          const input = el.querySelector<HTMLElement>(`input[value="${value}"]`);
          const label = input?.parentElement?.querySelector<HTMLElement>('.kf-a-dropdown__text');

          const markup = GetComponentMarkup(DropdownSelectionItem, {
            value,
            text: label?.innerText,
          });

          dropdownLabelText.innerHTML += markup;
        });
        const joinedValues = values.join(', ');

        el.dispatchEvent(
          new CustomEvent('dropdown-change', {
            detail: {
              value: joinedValues || '',
              text: option?.textContent,
              dataset: input?.dataset,
            },
          })
        );

        if (!values.length) {
          const placeholderInput = [...inputs].find((input) => input.disabled && !input.value);

          dropdownLabelText.removeAttribute('data-filled');

          if (!placeholderInput) {
            dropdownLabelText.textContent = '';
          } else {
            const placeholderLabel = placeholderInput.parentElement?.querySelector('label');

            dropdownLabelText.textContent = placeholderLabel ? placeholderLabel.innerText : '';
          }
        }

        if (optiDropdown) {
          const optiDropdownOptions = optiDropdown.querySelectorAll<HTMLOptionElement>('option');

          optiDropdownOptions.forEach((option) => {
            option.selected = values.includes(option.value);
          });

          // trigger change event, this is particular for Optimizely Forms
          optiDropdown.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }, 0);
    };

    const initOptionsEvent = (options: NodeListOf<HTMLElement>) => {
      if (!options?.length) return;

      [].forEach.call(options, (option: HTMLElement) => {
        option.querySelector<HTMLLabelElement>('label')?.addEventListener('click', (e) => {
          onOptionClick(option, e);
        });
        option.addEventListener('keydown', (e) => {
          e.stopPropagation();

          if (e.code !== 'Enter') return;

          option.querySelector('label')?.click();
        });
      });
    };

    const removeSelectItem = (element: HTMLElement) => {
      const input = el.querySelector<HTMLInputElement>(`input[value="${element.dataset?.value}"]`);

      if (!input) return;

      const option = input.closest<HTMLElement>(getClass('option'));

      if (!option) return;

      input.checked = false;
      handleCheckboxChange(option, input);
    };

    const triggerFocusOnTitleClick = () => {
      const dropdownField = el.closest<HTMLElement>('.kf-m-dropdown-field');

      const title = dropdownField?.querySelector<HTMLElement>('.kf-m-dropdown-field__title');

      title?.addEventListener('click', () => {
        el?.focus();
      });
    };

    initOptionsEvent(options);
    checkOptiFormVisible();
    handleTabSelectionView();
    triggerFocusOnTitleClick();

    if (isMultipleChoice) {
      handleCheckboxChange();
    } else {
      preselectSingleChoice();
    }

    el.addEventListener('init-options', () => {
      const options = el.querySelectorAll<HTMLElement>(getClass('option'));

      initOptionsEvent(options);
    });

    dropdownLabel.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;

      if (removeSelectionItemOnEnter(e, target)) return;

      toggleDropdown();
    });

    const removeSelectionItemOnEnter = (e: Event, target: HTMLElement) => {
      if (
        target.classList.contains('kf-a-dropdown-selection-item__remove-button') ||
        target.closest('.kf-a-dropdown-selection-item__remove-button')
      ) {
        e.preventDefault();
        e.stopPropagation();

        const selectionItem = target.closest<HTMLElement>('.kf-a-dropdown-selection-item');

        if (!selectionItem) return;
        removeSelectItem(selectionItem);

        return true;
      }

      return false;
    };

    dropdownLabel.addEventListener('keydown', (e) => {
      const target = e.target as HTMLElement;

      if (e.key === 'Enter') {
        e.preventDefault();
        removeSelectionItemOnEnter(e, target);
      }
    });

    el.addEventListener('keydown', (e) => {
      const target = e.target as HTMLElement;

      if (e.code === 'Enter' || (dropdownLabel.isSameNode(target) && e.code === 'Enter')) {
        toggleDropdown();
      }

      if (e.code === 'Backspace' && isMultipleChoice && el.dataset.tabViewEnabled !== 'true' && target.classList.contains('kf-a-dropdown__label')) {
        const selectionItems = target.querySelectorAll<HTMLElement>('.kf-a-dropdown-selection-item');

        if (!selectionItems.length) return;

        removeSelectItem(selectionItems[selectionItems.length - 1]);
      }
    });

    dropdownOptions.onOutsideClick(() => {
      closeDropdown();
    }, [dropdownLabel]);

    window.addEventListener('resize', () => {
      handleTabSelectionView();
    });
  },
};

window.KFDropdown.init();

export {};
